// These helpers are calling FTW's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';

import config from '../config';
import { logError } from './log';
import { isCordova } from './mobile/deviceDetection';
import { notifyNetworkTrouble } from './networkStatus';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development and inside cordova container, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return typeof window !== 'undefined'
    ? `${window.location.origin}`
    : process.env.REACT_APP_CANONICAL_ROOT_URL;
};

export const cordovaApiBaseUrl = () => {
  let API_BASE_URL = 'http://localhost:3000';
  if (process.env.REACT_APP_API === 'development') {
    API_BASE_URL = 'https://development.vansite.eu';
    if (process.env.REACT_APP_NGROK_URL) {
      API_BASE_URL = process.env.REACT_APP_NGROK_URL;
    }
  } else if (process.env.REACT_APP_API === 'production') {
    API_BASE_URL = 'https://vansite.eu';
  }
  return API_BASE_URL;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    sdkType: sdkTypes.BigDecimal,
    appType: Number,
    writer: (v) => new sdkTypes.BigDecimal(v.toString()),
    reader: (v) => Number(v.value),
  },
];

// The transit codec is loaded lazily (dynamic import) and memoized so it only
// hits the network/bundle once, keeping it out of the main bundle for routes
// that never talk to the local API.
let transitModulePromise = null;
const loadTransit = () =>
  transitModulePromise ??
  (transitModulePromise = import('@vansite/ts-sharetribe-flex-sdk/transit'));

const serialize = async (data) => {
  const transit = await loadTransit();
  return transit.write(data, { typeHandlers, verbose: config.sdk.transitVerbose });
};

const deserialize = async (str) => {
  const transit = await loadTransit();
  return transit.read(str, { typeHandlers });
};

// Client-side request timeout. A hung request (gateway not responding) would
// otherwise block until the platform gateway 504 (~30s) or the socket drops,
// leaving the user staring at a spinner. Abort it ourselves so the UI can show a
// clean "try again" (VANSITE-FRONTEND-49X).
const REQUEST_TIMEOUT_MS = 30000;

// Run fetch with a browser-side abort timeout and flag transient connectivity
// trouble (timeout / network failure / gateway 5xx) so the TopBar can surface a
// generic error. The SSR/node path skips the AbortController (the node-fetch
// shim used there has no signal support) and just performs the fetch.
// Caller-supplied signal (e.g. a superseded corridor fetch) aborts the same
// controller, so one fetch option carries both the timeout and the caller.
const linkAbortSignal = (controller, external) => {
  if (!controller || !external) return;
  if (external.aborted) controller.abort();
  else external.addEventListener('abort', () => controller.abort(), { once: true });
};

const fetchWithTimeout = (customWindow, url, options, timeoutMs = REQUEST_TIMEOUT_MS) => {
  const hasAbort = typeof window !== 'undefined' && typeof AbortController !== 'undefined';
  const controller = hasAbort ? new AbortController() : null;
  const external = options.signal;
  let timedOut = false;
  const timer = controller
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs)
    : null;
  linkAbortSignal(controller, external);
  const fetchOptions = controller ? { ...options, signal: controller.signal } : options;

  return customWindow.fetch(url, fetchOptions).then(
    (res) => {
      if (timer) clearTimeout(timer);
      // Gateway timeout / unavailability — transient infra, not an app bug.
      if (res.status === 502 || res.status === 503 || res.status === 504) {
        notifyNetworkTrouble();
      }
      return res;
    },
    (err) => {
      if (timer) clearTimeout(timer);
      if (external?.aborted && !timedOut) {
        // Caller cancelled on purpose — not a connectivity problem.
        throw err;
      }
      // Either our own timeout abort or a network-level failure ("Load failed")
      // — both mean the user lost connectivity to our backend mid-request.
      notifyNetworkTrouble();
      if (controller?.signal.aborted) {
        const e = new Error('Request timeout');
        e.name = 'TimeoutError';
        e.status = 0;
        throw e;
      }
      throw err;
    }
  );
};

// Shared response handling for the web post/get paths.
const parseApiResponse = (res) => {
  const contentTypeHeader = res.headers.get('Content-Type');
  const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

  if (res.status >= 400) {
    // Error responses are normally small JSON/transit bodies, but a gateway
    // timeout (502/503/504) returns an HTML error page. Calling res.json() on
    // that throws a misleading WebKit SyntaxError ("The string did not match
    // the expected pattern.") that masks the real cause and groups as a bogus
    // Sentry issue (VANSITE-FRONTEND-49X). Parse the body defensively: keep the
    // structured fields (e.g. error.code for the 409 conflict) when the body is
    // JSON, and fall back to a plain status error otherwise.
    return res.text().then((text) => {
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        // Non-JSON body (gateway HTML error page) — leave data empty.
      }
      let e = new Error(data.message || `Request failed with status ${res.status}`);
      e = Object.assign(e, data);
      e.status = res.status;
      throw e;
    });
  }
  if (contentType === 'application/transit+json') {
    return res.text().then(deserialize);
  } else if (contentType === 'application/json') {
    return res.json();
  }
  return res.text();
};

const post = (path, body, { signal } = {}) => {
  if (isCordova()) {
    return postCordova(path, body);
  } else {
    return postWeb(path, body, { signal });
  }
};

const postWeb = async (path, body, { signal } = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: /** @type {RequestCredentials} */ ('include'),
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: await serialize(body),
    ...(signal ? { signal } : {}),
  };

  let customWindow;
  if (typeof window === 'undefined') {
    customWindow = {
      fetch: require('node-fetch'),
    };
  } else {
    customWindow = window;
  }

  return fetchWithTimeout(customWindow, url, options).then(parseApiResponse);
};

// The invoice scan runs two Anthropic attempts server-side and can exceed
// the default 30 s request timeout — give uploads 90 s.
const UPLOAD_TIMEOUT_MS = 90000;

// Multipart POST for file uploads (FlexPass invoice PDF). The body is
// FormData — the browser sets the multipart boundary itself, so no
// Content-Type header may be set manually. Uses window.fetch in the Cordova
// WebView too (the cordova HTTP plugin has no clean FormData path); the dev
// Basic-auth header mirrors postCordova.
const postFormData = (path, formData) => {
  const cordova = isCordova();
  const url = `${cordova ? cordovaApiBaseUrl() : apiBaseUrl()}${path}`;
  const headers =
    cordova && process.env.REACT_APP_API === 'development'
      ? { Authorization: `Basic ${process.env.REACT_APP_DEVELOPMENT_AUTH}` }
      : {};
  const options = {
    method: 'POST',
    credentials: /** @type {RequestCredentials} */ ('include'),
    headers,
    body: formData,
  };
  return fetchWithTimeout(window, url, options, UPLOAD_TIMEOUT_MS).then(parseApiResponse);
};

// Build a descriptive message for an error response from the Cordova HTTP
// plugin. Errors constructed with an empty message are all titled
// "No error message" in Sentry and group into one undiagnosable issue per
// construction site (VANSITE-FRONTEND-47P/48T/48E et al.).
const cordovaErrorMessage = (res, data) => {
  const detail =
    (typeof data === 'string' && data) ||
    data?.message ||
    data?.detail ||
    data?.statusText ||
    (typeof res?.error === 'string' && res.error) ||
    '';
  const status = res?.status != null ? `status ${res.status}` : 'unknown status';
  return detail ? `${detail} (${status})` : `Request failed with ${status}`;
};

// Whether a failed Cordova HTTP response is worth a Sentry report. The web path
// (postWeb/getWeb) reports nothing at transport level — callers (ducks) report
// with their own code, e.g. 'signup-failed'. Reporting every 4xx here as well
// duplicated those and turned expected outcomes (wrong code, pass owned by
// another customer, validation) into noise (VANSITE-FRONTEND-4MG). 5xx and
// plugin-level failures (status <= 0 / missing: timeout, no connection) stay
// reported; the reject with status is unchanged either way.
export const shouldReportCordovaError = (res) => {
  const status = res?.status;
  return !(status >= 400 && status < 500);
};

// Shared rejection path for getCordova/postCordova error responses: turns the
// Cordova HTTP plugin's error shape into a rejected Error that keeps the
// response's structured fields and carries a descriptive message.
export const rejectWithCordovaError = (reject, res) => {
  // Carry the HTTP status onto the rejected Error. The web path (postWeb/getWeb)
  // already sets `e.status = res.status`, and log.js `isNonActionableError`
  // keys off it to drop expected 4xx (409 conflict, 429 rate-limit, Sharetribe
  // 404). Without this, every Cordova 4xx reached Sentry as un-filterable noise
  // (VANSITE-FRONTEND-4H4 subscription-already-active 409 et al.). `{ status }`
  // is spread last so the authoritative response status wins over any `status`
  // field embedded in the parsed body.
  const status = res?.status;
  try {
    const data = res.data || res.error;

    if (typeof data === 'string') {
      try {
        const object = JSON.parse(data);
        reject(Object.assign(new Error(cordovaErrorMessage(res, object)), object, { status }));
      } catch (e) {
        reject(Object.assign(new Error(cordovaErrorMessage(res, data)), { status }));
      }
    } else if (typeof data === 'object' && data !== null) {
      reject(Object.assign(new Error(cordovaErrorMessage(res, data)), data, { status }));
    } else {
      reject(Object.assign(new Error(cordovaErrorMessage(res, null)), { status }));
    }
  } catch (error) {
    reject(Object.assign(new Error('An error occurred while processing the response'), { status }));
  }
};

const postCordova = async (path, body) => {
  const url = `${cordovaApiBaseUrl()}${path}`;

  let headers = {};
  if (process.env.REACT_APP_API === 'development') {
    headers = {
      Authorization: `Basic ${process.env.REACT_APP_DEVELOPMENT_AUTH}`,
    };
  }

  const enc = new TextEncoder();
  const serializedBody = await serialize(body);
  const binaryBody = enc.encode(serializedBody);

  const generateKey = (clientId, namespace) => `${namespace}-${clientId}-token`;
  const namespace = 'st';
  const key = generateKey(config.sdk.clientId, namespace);
  const xCookie = `${key}=${encodeURIComponent(typeof window !== 'undefined' ? window.localStorage.getItem(key) || '' : '')}`;

  const options = {
    method: 'post',
    serializer: 'raw',
    data: binaryBody,
    headers: {
      'Content-Type': 'application/transit+json',
      'X-COOKIE': xCookie,
      ...headers,
    },
  };

  return new Promise((resolve, reject) => {
    const onSuccess = (res) => {
      console.log('[postCordova] onSuccess', res);

      if (res.status >= 400) {
        return onError(res);
      }

      const contentTypeHeader = res.headers['content-type'];
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

      switch (contentType) {
        case 'application/transit+json':
          return resolve(deserialize(res.data));
        case 'application/json':
          return resolve(JSON.parse(res.data));
        default:
          return resolve(res.data);
      }
    };

    const onError = (res) => {
      if (shouldReportCordovaError(res)) {
        logError(res, 'post-cordova-error');
      }
      rejectWithCordovaError(reject, res);
    };

    console.log('[postCordova] post', JSON.parse(JSON.stringify({ url, options, body })));

    const cordovaHttpPlugin = window?.cordova?.plugin?.http;

    const waitForCordova = () => {
      if (!cordovaHttpPlugin) {
        setTimeout(() => {
          waitForCordova();
        }, 100);
      } else {
        try {
          cordovaHttpPlugin.sendRequest(url, options, onSuccess, onError);
        } catch (e) {
          logError(e, 'cordovaHttpPlugin.sendRequest', { url, options, body });
        }
      }
    };
    waitForCordova();
  });
};

const get = (path, query) => {
  if (isCordova()) {
    return getCordova(path, query);
  } else {
    return getWeb(path, query);
  }
};

const getCordova = (path, query) => {
  const params = query ? `?${new URLSearchParams(query).toString()}` : '';
  const url = `${cordovaApiBaseUrl()}${path}${params}`;

  let headers = {};
  if (process.env.REACT_APP_API === 'development') {
    headers = {
      Authorization: `Basic ${process.env.REACT_APP_DEVELOPMENT_AUTH}`,
    };
  }

  const generateKey = (clientId, namespace) => `${namespace}-${clientId}-token`;
  const namespace = 'st';
  const key = generateKey(config.sdk.clientId, namespace);
  const xCookie = `${key}=${encodeURIComponent(typeof window !== 'undefined' ? window.localStorage.getItem(key) || '' : '')}`;

  const options = {
    method: 'get',
    headers: {
      Accept: 'application/transit+json',
      'X-COOKIE': xCookie,
      ...headers,
    },
  };

  return new Promise((resolve, reject) => {
    const onSuccess = (res) => {
      if (res.status >= 400) {
        return onError(res);
      }

      const contentTypeHeader = res.headers['content-type'];
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

      switch (contentType) {
        case 'application/transit+json':
          return resolve(deserialize(res.data));
        case 'application/json':
          return resolve(JSON.parse(res.data));
        default:
          return resolve(res.data);
      }
    };

    const onError = (res) => {
      if (shouldReportCordovaError(res)) {
        logError(res, 'get-cordova-error');
      }
      rejectWithCordovaError(reject, res);
    };

    const cordovaHttpPlugin = window?.cordova?.plugin?.http;

    const waitForCordova = () => {
      if (!cordovaHttpPlugin) {
        setTimeout(() => {
          waitForCordova();
        }, 100);
      } else {
        try {
          cordovaHttpPlugin.sendRequest(url, options, onSuccess, onError);
        } catch (e) {
          logError(e, 'cordovaHttpPlugin.sendRequest', { url, options });
        }
      }
    };
    waitForCordova();
  });
};

const getWeb = (path, query) => {
  const params = query ? `?${new URLSearchParams(query).toString()}` : '';
  const url = `${apiBaseUrl()}${path}${params}`;
  const options = {
    method: 'GET',
    credentials: /** @type {RequestCredentials} */ ('include'),
    headers: {
      Accept: 'application/transit+json',
    },
  };

  let customWindow;
  if (typeof window === 'undefined') {
    customWindow = {
      fetch: require('node-fetch'),
    };
  } else {
    customWindow = window;
  }

  return fetchWithTimeout(customWindow, url, options).then(parseApiResponse);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = (body) => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the booking data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = (body) => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = (body) => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Flex with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = (body) => {
  return post('/api/auth/create-user-with-idp', body);
};
/**
 * @param {object} body
 * @param {object} body.createUserParams
 * @param {boolean} body.approvedForNewsletter
 * @param {string | null} body.referralId
 * @param {object | null} [body.partnerRef]
 * @param {{marketing: boolean}} [body.consent]
 * @param {ReturnType<typeof import('./consent/trackingIds').collectTrackingIds>} [body.ids]
 *   fbp/fbc/GA ids — only sent with marketing consent (CAPI CompleteRegistration)
 * @param {ReturnType<typeof import('./consent/attribution').getAttribution>} [body.attribution]
 *   first/last touch incl. fbclid + ts — only sent with marketing consent
 * @param {string} [body.url] page URL (CAPI event_source_url) — only sent with marketing consent
 * @returns {Promise}
 */
export const backEndSignUp = (body) => {
  return post('/api/auth/signup', body);
};

// Partner-Ref (Haspa-Kooperation): trägt die gespeicherte Partner-Referenz
// beim eingeloggten User nach. Server-seitig first-wins (409 wenn vorhanden).
export const submitPartnerRef = (body) => {
  return post('/api/vansite/partner-ref', body);
};

// Login-Proxy (vansite-auth, ADR-002): der Sharetribe-Grant läuft serverseitig,
// das Antwort-Cookie ist SDK-kompatibel (BrowserStore liest es); Cordova läuft
// wie alle post()-Calls über den X-COOKIE-Bypass. 409 = Server-Flag aus.
export const proxyLogin = (body) => {
  return post('/api/auth/login', body);
};

// Auth-Client-Konfiguration (vansite-auth): liefert die Auth-Epoch für den
// einmaligen Zwangs-Logout (s. enforceAuthEpoch in ducks/auth.duck.js).
export const getAuthConfig = () => {
  return get('/api/auth/config');
};

// Frontend build the server renders (`{ data: { build } }`), see util/staleBuildGuard.js.
export const getBuild = () => {
  return get('/api/build');
};

// Route-planner corridor search (docs/specs/ROUTE_CORRIDOR_SEARCH.md §7).
// 404 = server flag off; the duck falls back to the legacy Sharetribe path
// on any rejection that is not an abort.
export const queryRouteCorridor = (body, { signal } = {}) => {
  return post('/api/route-planner/corridor', body, { signal });
};

// Dual-write Passwort-/E-Mail-Pfade über das BFF (marketplace-web@P1).
export const proxyPasswordReset = (body) => {
  return post('/api/auth/password-reset', body);
};

export const proxyPasswordChange = (body) => {
  return post('/api/auth/password-change', body);
};

export const proxyEmailChange = (body) => {
  return post('/api/auth/email-change', body);
};

export const checkForUserEmail = (body) => {
  return post('/api/auth/check-user-email', body);
};

// Create custom stripe setup intent
//
// It is used because sharetirbe is not allowing to create custom
// payment methods at this time (10.2020)
export const stripeSetupIntent = (body) => {
  return post('/api/stripe/setup-intents', body);
};

export const stripeRetrieveSetupIntent = (body) => {
  return post('/api/stripe/retrieve-setup-intent', body);
};

export const stripeCreateCustomer = (body) => {
  return post('/api/stripe/create-customer', body);
};

export const stripeAddPaymentMethod = (body) => {
  return post('/api/stripe/add-payment-method', body);
};

export const stripeDeletePaymentMethod = (body) => {
  return post('/api/stripe/delete-payment-method', body);
};

export const stripeCancelPaymentIntent = (body) => {
  return post('/api/stripe/cancel-payment-intent', body);
};

export const stripeUpdatePaymentIntent = (body) => {
  return post('/api/stripe/update-payment-intent', body);
};

export const stripeUpdateCustomer = (body) => {
  return post('/api/stripe/update-customer', body);
};

export const stripeSearchProduct = (body) => {
  return post('/api/stripe/search-product', body);
};

export const stripeCreatePortalSession = (body) => {
  return post('/api/stripe/create-portal-session', body);
};

// Subscription

export const subscriptionFetch = (body) => {
  return post('/api/subscription/fetch', body);
};

export const subscriptionCreateSession = (body) => {
  return post('/api/subscription/checkout', body);
};

export const subscriptionCancel = (body) => {
  return post('/api/subscription/cancel', body);
};

export const subscriptionFulfill = (body) => {
  return post('/api/subscription/fulfill', body);
};

export const subscriptionSchedule = (body) => {
  return post('/api/subscription/schedule', body);
};

export const subscriptionNextPeriodCheckout = (body) => {
  return post('/api/subscription/next-period-checkout', body);
};

export const subscriptionNextPeriodFulfill = (body) => {
  return post('/api/subscription/next-period-fulfill', body);
};

export const subscriptionUpgradeCheckout = (body) => {
  return post('/api/subscription/upgrade-checkout', body);
};

export const subscriptionUpgradeFulfill = (body) => {
  return post('/api/subscription/upgrade-fulfill', body);
};

export const subscriptionReconcileExisting = (body) => {
  return post('/api/subscription/reconcile-existing', body);
};

export const subscriptionPassChangeCheckout = (body) => {
  return post('/api/subscription/pass-change-checkout', body);
};

export const subscriptionRenewalVoucherApply = (body) => {
  return post('/api/subscription/renewal-voucher/apply', body);
};

export const subscriptionRenewalVoucherRemove = () => {
  return post('/api/subscription/renewal-voucher/remove', {});
};

export const subscriptionPassPrices = (body) => {
  return post('/api/subscription/pass-prices', body);
};

// VanSite
export const vansiteCreatePaymentIntent = (body) => {
  return post('/api/vansite/create-payment-intent', body);
};

export const vansiteConfirmPaymentIntent = (body) => {
  return post('/api/vansite/confirm-payment-intent', body);
};

export const vansiteCapturePaymentIntent = (body) => {
  return post('/api/vansite/capture-payment-intent', body);
};

export const vansiteDeleteUser = (body) => {
  return post('/api/vansite/delete-user', body);
};

export const vansiteUpdateListingRating = (body) => {
  return post('/api/vansite/update-listing-rating', body);
};

export const vansiteCancelTransaction = (body) => {
  return post('/api/vansite/cancel-transaction', body);
};

/**
 * What cancelling the transaction right now would refund, computed server side
 * from the exact line items `vansiteCancelTransaction` would send. Nothing is
 * transitioned. Party-only, enforced by the endpoint with a structured 403.
 *
 * @param {{ transactionId: string }} body
 * @returns {Promise<{ data: { refund: Money, cancellationFee: Money, refundedNights: number, percentage: number | undefined } }>}
 */
export const vansiteCancelTransactionPreview = (body) => {
  return post('/api/vansite/cancel-transaction-preview', body);
};

export const vansiteUpdateTransactionMetadata = (body) => {
  return post('/api/vansite/update-transaction-metadata', body);
};

export const vansiteUpdateNotificationStatus = (body) => {
  return post('/api/vansite/update-notification-status', body);
};

export const icalEnsureToken = (body) => {
  return post('/api/ical/ensure-token', body);
};

export const vansiteWithdrawTransaction = (body) => {
  return post('/api/vansite/withdraw-transaction', body);
};

export const vansiteAbortUnpaidTransaction = (body) => {
  return post('/api/vansite/abort-unpaid-transaction', body);
};

export const vansiteInsuranceForm = (body) => {
  return post('/api/vansite/insurance-form', body);
};

/**
 * Reconciles a camping ground's derived price and closing-period availability
 * exceptions immediately instead of waiting for the nightly scheduler run.
 * Owner-only; a failure is safe to ignore because the nightly run self-heals.
 *
 * @param {{ listingId: string }} body
 */
export const campingGroundSync = (body) => {
  return post('/api/vansite/camping-ground-sync', body);
};

/**
 * Resolves the guest's full name for a booking. The Marketplace API only
 * exposes `displayName` ("Erika M.") for users you are not, so this goes
 * through the Integration SDK server side.
 *
 * Provider-only and only from the `accepted` state onwards — both enforced by
 * the endpoint, which rejects everything else with a structured 403.
 *
 * @param {{ transactionId: string }} body
 * @returns {Promise<{ firstName: string | null, lastName: string | null }>}
 */
export const vansiteTransactionCustomerName = (body) => {
  return post('/api/vansite/transaction-customer-name', body);
};

/**
 * Loads the host's automatic welcome message for a booking.
 *
 * Party-only (guest or host), enforced by the endpoint with a structured 403.
 * The message version and listing come from the transaction server side, so
 * the body carries only the transaction id.
 *
 * @param {{ transactionId: string }} body
 * @returns {Promise<{ data: { text: string, sourceLocale: string, translations: Record<string, string>, createdAt: string } | null }>}
 */
export const vansiteTransactionWelcomeMessage = (body) => {
  return post('/api/vansite/transaction-welcome-message', body);
};

/**
 * Requests a short-lived (60 s) download URL for one attachment of the host's
 * welcome message.
 *
 * Party-only (guest or host), enforced by the endpoint with a structured 403;
 * an unknown attachment id answers with `{ error: 'attachment-not-found' }`.
 *
 * @param {{ transactionId: string, attachmentId: string, disposition: 'inline' | 'attachment' }} body
 * @returns {Promise<{ data: { url: string } }>}
 */
export const vansiteTransactionWelcomeAttachment = (body) => {
  return post('/api/vansite/transaction-welcome-attachment', body);
};

/**
 * Uploads one attachment for the host's automatic welcome message.
 *
 * Owner-only, enforced by the endpoint. Errors arrive as plain JSON
 * `{ error: '<code>' }` (e.g. `invalid-file-type`, `invalid-file`), so the
 * rejected Error carries the code in `error`; a proxy 413 has only `status`.
 *
 * @param {FormData} formData multipart fields `file` and `listingId`
 * @returns {Promise<{ data: { id: string, name: string, contentType: string, size: number } }>}
 */
export const vansiteUploadWelcomeAttachment = (formData) => {
  return postFormData('/api/vansite/listing-welcome-attachment', formData);
};

// export const vansiteGetGeolocationJson = body => {
//   return post('/api/vansite/load-geolocation-json', body);
// };

// vansite-mail

/**
 * Spiegelt den aktuellen Profilstand nach vansite-mail. `previousEmail` sorgt beim
 * E-Mail-Wechsel dafür, dass der Kontakt zur alten Adresse entfernt wird.
 *
 * @param {{ previousEmail?: string }} body
 */
export const vansiteMailSyncContact = (body) => {
  return post('/api/vansite-mail/sync-contact', body);
};

/**
 * Meldet ein Kontakt-Ereignis; der Server leitet daraus die
 * vansite-mail-Listenänderungen ab (server/util/mailContactLists.js).
 *
 * @param {{ event: 'email-verified'|'listing-draft-created'|'listing-published'|'notification-settings-updated'|'annual-pass-expired' }} body
 */
export const vansiteMailContactEvent = (body) => {
  return post('/api/vansite-mail/contact-event', body);
};

// Voucherify
export const voucherifyValidateVoucher = (body) => {
  return post('/api/voucherify/validate-voucher', body);
};

/**
 * Rolls the Voucherify redemption of a declined/cancelled booking back. The
 * server only accepts a redemption id that the given transaction recorded, and
 * only from one of its parties — so the transaction id is mandatory.
 *
 * @param {{ id: string, transactionId: string }} body
 */
export const voucherifyRollbackRedemption = (body) => {
  return post('/api/voucherify/rollback-redemption', body);
};

export const voucherifyGetVoucher = (body) => {
  return post('/api/voucherify/get-voucher', body);
};

export const joinVoucherifyLoyaltyProgram = (body) => {
  return post('/api/voucherify/loyalty/join-programm', body);
};

export const getVoucherifyRewards = (body) => {
  return post('/api/voucherify/loyalty/get-rewards', body);
};

export const redeemVoucherifyReward = (body) => {
  return post('/api/voucherify/loyalty/redeem-reward', body);
};

export const getVoucherifyTasks = (body) => {
  return post('/api/voucherify/loyalty/get-tasks', body);
};

export const completeVoucherifyTask = (body) => {
  return post('/api/voucherify/loyalty/complete-task', body);
};

// Events

export const eventTracking = (body) => {
  return post('/api/events/tracking', body);
};

export const consentTelemetry = (body) => {
  return post('/api/consent/telemetry', body);
};

export const consentIdentity = (body) => {
  return post('/api/consent/identity', body);
};

// Google

export const translateText = (body) => {
  return post('/api/google/translate-text', body);
};

// Annual Pass

export const passActivate = (body) => {
  return post('/api/annual-pass/activate', body);
};

export const passCheck = (body) => {
  return post('/api/annual-pass/check', body);
};

// Flex Pass

export const validateFlexPassBooking = (body) => {
  return post('/api/flex-pass/validate-booking', body);
};

export const getFlexPassPartners = () => {
  return get('/api/flex-pass/partners');
};

/** @param formData {FormData} - fields: invoice (File), companyId, returnUrl, originContext */
export const validateFlexPassInvoice = (formData) => {
  return postFormData('/api/flex-pass/validate-invoice', formData);
};

// OneSignal

/**
 * @param body {object}
 * @param body.tags {object} - OneSignal tags
 * @returns {Promise} - the server resolves the OneSignal external user id from
 *   the authenticated session; a client-supplied one is ignored
 */
export const updateOneSignalTags = (body) => {
  return post('/api/onesignal/update-tags', body);
};

// Newsletter
export const subscribeToNewsletter = (body) => {
  return post('/api/newsletter/subscribe', body);
};

// Translation

export const getTranslation = (body) => {
  return post('/api/translation/get', body);
};

export const putTranslation = (body) => {
  return post('/api/translation/put', body);
};

export const getOnDemandTranslation = (body) => {
  return post('/api/translation/on-demand', body);
};

// PhoneNumber Verification
export const createVerificationCode = (body) => {
  return post('/api/auth/phone-number/create-verification-code', body);
};
export const verifyVerificationCode = (body) => {
  return post('/api/auth/phone-number/verify-verification-code', body);
};

export const contactSupport = (body) => {
  return post('/api/contact/support', body);
};

export const sendContactInquiry = (body) => {
  return post('/api/contact/inquiry', body);
};

/*                  _               ____
 /\   /\___ _ __ ___(_) ___  _ __   |___ \
 \ \ / / _ \ '__/ __| |/ _ \| '_ \    __) |
  \ V /  __/ |  \__ \ | (_) | | | |  / __/
   \_/ \___|_|  |___/_|\___/|_| |_| |_____|
*/

export const createBookingSummary = (body) => {
  return post('/v2/api/booking-summary', body);
};

export const retrieveProviderBalance = (body) => {
  return post('/v2/api/provider-balance', body);
};

// Dump Stations

export const searchDumpStations = (params) => {
  return get('/api/dump-stations', params);
};

export const showDumpStation = (id) => {
  return get(`/api/dump-stations/${id}`);
};
